import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DirectivesModule} from '@app/shared/directives/directives.module';
import {PinchZoomDirective} from '@app/shared/directives/pinch-zoom.directive';

export interface V2LightboxImage {
  src: string;
  /** Optional filename for the download link; falls back to the URL basename. */
  downloadName?: string;
  /** Marks a CarPeak / commercial photo — shows the ★ CARPEAK tag. */
  isPremium?: boolean;
}

/**
 * Full-screen image viewer shared across the v2 vehicle tabs (Photos tab and
 * the Procurement "Slike ob nabavi vozila" gallery). Prev/next nav, keyboard
 * control, download, and zoom (pinch/pan on touch via `appPinchZoom`, +/− and
 * mouse-wheel on desktop). Open by binding `[(index)]`: set it to a photo
 * index to open, -1 to close.
 *
 * The `.v2-lightbox` styles live globally in `styles.scss` (un-nested from the
 * photos tab), so this component ships no scss of its own.
 */
@Component({
  selector: 'app-v2-lightbox',
  standalone: true,
  imports: [CommonModule, DirectivesModule],
  templateUrl: './v2-lightbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2LightboxComponent {
  @Input() images: V2LightboxImage[] = [];
  /** -1 = closed. Two-way bound: the parent sets the open index, this updates
   *  it on prev/next/close. */
  @Input() index = -1;
  @Output() indexChange = new EventEmitter<number>();
  /** Alt-text prefix for the image (index is appended). */
  @Input() alt = $localize`Fotografija`;

  @ViewChild(PinchZoomDirective) private pinchZoom?: PinchZoomDirective;
  @ViewChildren('thumbnail') private thumbnails?: QueryList<ElementRef<HTMLElement>>;

  constructor(private cdr: ChangeDetectorRef) {}

  get isOpen(): boolean {
    return this.index >= 0 && this.index < this.images.length;
  }

  current(): V2LightboxImage | null {
    return this.images[this.index] ?? null;
  }

  currentSrc(): string | null {
    return this.current()?.src ?? null;
  }

  altText(): string {
    return `${this.alt} ${this.index + 1}`;
  }

  isPremiumSlot(): boolean {
    return !!this.current()?.isPremium;
  }

  hasPrev(): boolean { return this.index > 0; }
  hasNext(): boolean { return this.index < this.images.length - 1; }

  close(): void { this.setIndex(-1); }
  prev(): void { if (this.hasPrev()) { this.setIndex(this.index - 1); } }
  next(): void { if (this.hasNext()) { this.setIndex(this.index + 1); } }

  /** Jump directly to a specific photo — used by the thumbnail strip. Ignores
   *  an out-of-range index instead of throwing, since thumbnails are rendered
   *  from the same `images` array so this should never actually happen. */
  goTo(i: number): void {
    if (i < 0 || i >= this.images.length) { return; }
    this.setIndex(i);
  }

  private setIndex(i: number): void {
    this.index = i;
    // Reset zoom/pan whenever the shown image changes or the viewer closes.
    this.pinchZoom?.resetTransform();
    this.indexChange.emit(i);
    this.scrollActiveThumbnailIntoView();
    this.cdr.markForCheck();
  }

  /** Keeps the active thumbnail visible in the strip as the user prev/next's
   *  through a large gallery — without this, a few presses scroll it out of
   *  view. Mirrors `field-items-form-dialog.component.ts`'s
   *  `scrollActiveThumbnailIntoView`. */
  private scrollActiveThumbnailIntoView(): void {
    setTimeout(() => {
      const thumbnail = this.thumbnails?.toArray()[this.index];
      thumbnail?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
    }, 0);
  }

  zoomIn(): void { this.pinchZoom?.zoomIn(); }
  zoomOut(): void { this.pinchZoom?.zoomOut(); }

  /** Download filename: explicit `downloadName`, else the URL basename, else
   *  a generic localised fallback. */
  currentDownloadName(): string {
    const img = this.current();
    if (img?.downloadName) { return img.downloadName; }
    const src = img?.src;
    if (!src) { return $localize`fotografija`; }
    try {
      const path = new URL(src, window.location.origin).pathname;
      const last = decodeURIComponent(path.split('/').filter(Boolean).pop() ?? '');
      if (last) { return last; }
    } catch { /* ignore — fall through */ }
    return $localize`fotografija`;
  }

  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.isOpen) { return; }
    if (e.key === 'Escape') { this.close(); }
    else if (e.key === 'ArrowLeft') { this.prev(); }
    else if (e.key === 'ArrowRight') { this.next(); }
  }
}
